// A single price-free product card — image, name, generated description,
// "Request price" — shared by pages/LandingPage.jsx (featured products) and
// pages/CatalogBrowsePage.jsx (category browse) so the two screens' cards
// can't silently drift apart.
//
// Condition is NOT shown as a separate line: generateDisplayDescription()
// (backend/src/utils/catalogDisplayDescription.js) always ends
// display_description with the human condition label already (e.g. "...,
// New" / "..., Refurbished (In-house)") — a standalone condition badge next
// to that was a real duplicate, fixed here. The badge only reappears as a
// fallback for the rare product with no display_description at all (the
// schema allows NULL), so condition information is never lost outright.
//
// Image: 4:3 area, `loading="lazy"` (the grid this renders in is usually
// below the fold). image_url is null for any product without a photo yet —
// renders the product's own top-level CATEGORY icon in that case (the same
// icon its category tile uses, see utils/categoryIcons.js), never a broken
// <img> and never a generic "image missing" glyph — a buyer scanning a
// grid of placeholders can still tell a laptop from a printer at a glance.
// `categoryIcon` is resolved by the caller (pages/LandingPage.jsx, pages/
// CatalogBrowsePage.jsx — both already have the full category tree loaded
// for their own category tiles/browse tree) rather than re-fetched here.
//
// `variant="featured"` (LandingPage.jsx's featured grid only — see the
// branch below): no price-free behavior changes, just a richer, clickable
// card that opens /catalog/:id and drops "Request price" in favor of "Add
// to request" alone. `categoryName` is resolved by the caller the same way
// `categoryIcon` already is (utils/categoryIcons.js's getCategoryNameForId).

import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { conditionLabelId, productDisplayName } from '../utils/productDisplay.js';
import { DEFAULT_CATEGORY_ICON } from '../utils/categoryIcons.js';
import styles from './ProductCard.module.css';

export default function ProductCard({
  product,
  onRequestPrice,
  onAddToRequest,
  categoryIcon: CategoryIcon,
  categoryName,
  dense,
  variant,
}) {
  const { t, lang } = useLanguage();
  const name = productDisplayName(product, lang);
  const Icon = CategoryIcon || DEFAULT_CATEGORY_ICON;

  const imageSrc = product.image_url || product.brand_image_url;
  const imageNode = imageSrc ? (
    <img
      className={styles.image}
      src={imageSrc}
      alt={name}
      loading="lazy"
      width={400}
      height={300}
    />
  ) : (
    <div className={styles.imagePlaceholder} aria-hidden="true">
      <Icon size={28} />
    </div>
  );

  // `variant="featured"` — LandingPage.jsx's featured-products grid only.
  // Opens the product detail page on click (the name/image/spec block is a
  // real <a> via <Link>, so Enter/keyboard activation and screen readers
  // work for free — no custom role/tabIndex needed) and drops "Request
  // price" entirely, keeping only "Add to request" as a sibling BUTTON
  // outside that link (never a button nested inside an <a>).
  // CatalogBrowsePage.jsx never passes this prop, so its cards are
  // untouched below.
  if (variant === 'featured') {
    const brandModel = [product.brand, product.model].filter(Boolean).join(' ');
    return (
      <li className={`${styles.card} ${dense ? styles.dense : ''}`}>
        <Link to={`/catalog/${product.id}`} className={styles.cardLink}>
          <div className={styles.imageArea}>{imageNode}</div>
          <div className={styles.body}>
            <p className={styles.name}>{name}</p>
            {brandModel && <p className={styles.brandModel}>{brandModel}</p>}
            {product.display_description ? (
              <p className={styles.description}>{product.display_description}</p>
            ) : (
              <span className={styles.conditionBadge}>
                {t(conditionLabelId(product.condition))}
              </span>
            )}
            {categoryName && <p className={styles.categoryLine}>{categoryName}</p>}
          </div>
        </Link>
        <div className={styles.cardActions}>
          <button
            type="button"
            className={styles.requestPriceButton}
            onClick={(e) => {
              e.stopPropagation();
              onAddToRequest();
            }}
          >
            {t('portal.catalog.addToRequest')}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={`${styles.card} ${dense ? styles.dense : ''}`}>
      <div className={styles.imageArea}>{imageNode}</div>

      <div className={styles.body}>
        <p className={styles.name}>{name}</p>
        {product.sku && (
          <p className={styles.sku}>{t('portal.catalog.productSku', { value: product.sku })}</p>
        )}
        {product.display_description ? (
          <p className={styles.description}>{product.display_description}</p>
        ) : (
          <span className={styles.conditionBadge}>{t(conditionLabelId(product.condition))}</span>
        )}
        <button type="button" className={styles.requestPriceButton} onClick={onRequestPrice}>
          {t('portal.catalog.requestPrice')}
        </button>
        {/* Entry Point 2 (catalog-picker task) — stays on this page, unlike
            Request price above (navigates to /start immediately). Same
            button styling reused rather than a second class: both are
            equally legitimate actions on this card, not a primary/
            secondary pair. */}
        <button type="button" className={styles.requestPriceButton} onClick={onAddToRequest}>
          {t('portal.catalog.addToRequest')}
        </button>
      </div>
    </li>
  );
}
